import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Define AnalyticsEvents locally since we removed the firebase file
export const AnalyticsEvents = {
  SEARCH_PERFORMED: 'search_performed',
  FILTER_APPLIED: 'filter_applied',
  EXPORT_DATA: 'export_data',
  REPORT_GENERATED: 'report_generated',
  TRANSACTION_ADDED: 'transaction_added',
  TRANSACTION_EDITED: 'transaction_edited',
  TRANSACTION_DELETED: 'transaction_deleted',
  SETTINGS_CHANGED: 'settings_changed',
};

// Dummy track functions
export const trackEvent = (eventName: string, params?: any) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] Event: ${eventName}`, params);
  }
};

export const trackPageView = (pageName: string) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics] Page View: ${pageName}`);
  }
};

/**
 * Custom hook for tracking analytics events in components
 * @returns Object with track methods
 */
const useAnalytics = () => {
  const location = useLocation();

  // Automatically track page views when the location changes
  useEffect(() => {
    // Extract page name from the path
    const pageName = location.pathname.split('/').pop() || 'home';
    trackPageView(pageName);
  }, [location]);

  return {
    /**
     * Track any custom event
     */
    trackEvent,

    /**
     * Track when a user performs a search
     */
    trackSearch: (searchTerm: string, category?: string) => {
      trackEvent(AnalyticsEvents.SEARCH_PERFORMED, {
        search_term: searchTerm,
        category
      });
    },

    /**
     * Track when a user applies filters
     */
    trackFilter: (filterType: string, filterValue: string) => {
      trackEvent(AnalyticsEvents.FILTER_APPLIED, {
        filter_type: filterType,
        filter_value: filterValue
      });
    },

    /**
     * Track when a user exports data
     */
    trackExport: (exportType: string, recordCount?: number) => {
      trackEvent(AnalyticsEvents.EXPORT_DATA, {
        export_type: exportType,
        record_count: recordCount
      });
    },

    /**
     * Track when a user generates a report
     */
    trackReportGeneration: (reportType: string) => {
      trackEvent(AnalyticsEvents.REPORT_GENERATED, { report_type: reportType });
    },

    /**
     * Track when a transaction is added
     */
    trackTransactionAdded: (transactionType: string, amount?: number) => {
      trackEvent(AnalyticsEvents.TRANSACTION_ADDED, {
        transaction_type: transactionType,
        amount
      });
    },

    /**
     * Track when a transaction is edited
     */
    trackTransactionEdited: (transactionType: string, transactionId: string) => {
      trackEvent(AnalyticsEvents.TRANSACTION_EDITED, {
        transaction_type: transactionType,
        transaction_id: transactionId
      });
    },

    /**
     * Track when a transaction is deleted
     */
    trackTransactionDeleted: (transactionType: string) => {
      trackEvent(AnalyticsEvents.TRANSACTION_DELETED, { transaction_type: transactionType });
    },

    /**
     * Track when settings are changed
     */
    trackSettingsChanged: (settingName: string, newValue: any) => {
      trackEvent(AnalyticsEvents.SETTINGS_CHANGED, {
        setting_name: settingName,
        new_value: typeof newValue === 'object' ? JSON.stringify(newValue) : newValue
      });
    }
  };
};

export default useAnalytics; 